import { useCallback, useEffect, useMemo, useState } from "react";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";

export type AreaNumberInputProps = {
  id?: string;
  value: number; // Value in square millimeters
  onChange?: (value: number) => void;
  unit?: AreaUnit; // Fixed unit if provided, does not allow other selection
  defaultUnit?: AreaUnit; // default displayed unit, does allow selection of other units
  className?: string;
  disabled?: boolean;
  locale?: string;
};

export function AreaNumberInput({
  id,
  value,
  onChange,
  unit,
  defaultUnit,
  className = "",
  disabled = false,
  locale = "de-DE",
}: AreaNumberInputProps) {
  const [selectedUnit, setSelectedUnit] = useState<AreaUnit>(
    defaultUnit || AreaUnit.SquareCentimeters,
  );
  const [inputValue, setInputValue] = useState<string>("0");

  const activeUnit = unit || selectedUnit;

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [locale],
  );

  const formatLocaleNumber = useCallback(
    (number: number) => formatter.format(Number.isNaN(number) ? 0 : number),
    [formatter],
  );

  const parseLocaleNumber = useCallback(
    (input: string) => {
      const separator = formatter.format(1.1).charAt(1);
      const normalized = input.replace(separator, ".");
      return parseFloat(normalized);
    },
    [formatter],
  );

  const convertFromSquareMillimeters = useCallback(
    (mm2: number, u: AreaUnit) => {
      switch (u) {
        case AreaUnit.SquareCentimeters:
          return mm2 / 100;
        case AreaUnit.SquareMeters:
          return mm2 / 1e6;
        case AreaUnit.Hectares:
          return mm2 / 1e10;
        default:
          return mm2;
      }
    },
    [],
  );

  const convertToSquareMillimeters = useCallback((val: number, u: AreaUnit) => {
    switch (u) {
      case AreaUnit.SquareCentimeters:
        return val * 100;
      case AreaUnit.SquareMeters:
        return val * 1e6;
      case AreaUnit.Hectares:
        return val * 1e10;
      default:
        return val;
    }
  }, []);

  useEffect(() => {
    setInputValue(
      formatLocaleNumber(convertFromSquareMillimeters(value, activeUnit)),
    );
  }, [value, activeUnit, formatLocaleNumber, convertFromSquareMillimeters]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleBlurOrEnter = () => {
    const parsedValue = parseLocaleNumber(inputValue);
    if (!Number.isNaN(parsedValue)) {
      const mm2 = convertToSquareMillimeters(parsedValue, activeUnit);
      const roundedValue = convertFromSquareMillimeters(mm2, activeUnit);
      setInputValue(formatLocaleNumber(roundedValue));
      onChange?.(mm2);
    } else {
      setInputValue(
        formatLocaleNumber(convertFromSquareMillimeters(value, activeUnit)),
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleBlurOrEnter();
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Input
        id={id}
        type="text"
        inputMode="decimal"
        pattern="[0-9]*[.,]?[0-9]*"
        className="h-8 w-24 text-end"
        value={inputValue}
        disabled={disabled}
        onChange={handleInputChange}
        onBlur={handleBlurOrEnter}
        onKeyDown={handleKeyDown}
      />
      {unit ? (
        <span className="flex h-8 items-center px-1">
          {AreaUnitUtil.toString(unit)}
        </span>
      ) : (
        <Select
          value={selectedUnit}
          defaultValue={defaultUnit || AreaUnit.SquareCentimeters}
          onValueChange={(val) => setSelectedUnit(val as AreaUnit)}
        >
          <SelectTrigger className="h-8 w-fit">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={AreaUnit.SquareMillimeters}>
              {AreaUnitUtil.toString(AreaUnit.SquareMillimeters)}
            </SelectItem>
            <SelectItem value={AreaUnit.SquareCentimeters}>
              {AreaUnitUtil.toString(AreaUnit.SquareCentimeters)}
            </SelectItem>
            <SelectItem value={AreaUnit.SquareMeters}>
              {AreaUnitUtil.toString(AreaUnit.SquareMeters)}
            </SelectItem>
            <SelectItem value={AreaUnit.Hectares}>
              {AreaUnitUtil.toString(AreaUnit.Hectares)}
            </SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

export enum AreaUnit {
  SquareMillimeters = "square-millimeters",
  SquareCentimeters = "square-centimeters",
  SquareMeters = "square-meters",
  Hectares = "hectares",
}

export class AreaUnitUtil {
  private static readonly descriptions: {
    [key in AreaUnit]: string;
  } = {
    [AreaUnit.SquareMillimeters]: "Quadratmillimeter",
    [AreaUnit.SquareCentimeters]: "Quadratzentimeter",
    [AreaUnit.SquareMeters]: "Quadratmeter",
    [AreaUnit.Hectares]: "Hektar",
  };

  private static readonly abbreviations: {
    [key in AreaUnit]: string;
  } = {
    [AreaUnit.SquareMillimeters]: "mm²",
    [AreaUnit.SquareCentimeters]: "cm²",
    [AreaUnit.SquareMeters]: "m²",
    [AreaUnit.Hectares]: "ha",
  };

  static toString(type: AreaUnit): string {
    return this.abbreviations[type];
  }

  static toLongString(type: AreaUnit): string {
    return this.descriptions[type] || "Unknown";
  }
}
